import {
  completeQueryData,
  hasCompleteQueryData,
  throwOnQueryError,
} from 'ZalamoLibrary/src/lib/core/graphql/complete-query-data';

import {
  BehaviorSubject,
  EMPTY,
  Subject,
  catchError,
  combineLatest,
  concatMap,
  debounceTime,
  filter,
  first,
  fromEvent,
  map,
  merge,
  pipe,
  takeUntil,
  tap,
} from 'rxjs';

import { Injectable, OnDestroy } from '@angular/core';

import { VisualGuideService } from './visual-guide.service';
import { VisualGuideSteps } from './types';
import {
  GetAllAvailableVisualGuidesGQL,
  GetMineVisualGuidesStateGQL,
  VisualGuideFront,
  VisualGuideStepFront,
} from './graphql';

interface GuideRegistration {
  /** The DOM container name, not the persisted visualGuideId. */
  visualGuideId: string;
  node: HTMLElement;
}
interface PendingGuide {
  container: string;
  registeredDuringGuide: boolean;
}
interface ReadyGuide extends PendingGuide {
  node: HTMLElement;
  steps: VisualGuideSteps;
  alreadyStarted: boolean;
}
interface GuideLabel {
  name: string;
  container: string;
}
type GuideDefinition = Partial<
  Omit<VisualGuideFront, '__typename' | 'steps'>
> & {
  steps?: ReadonlyArray<Partial<
    Omit<VisualGuideStepFront, '__typename'>
  > | null> | null;
};
const isAction = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0;

@Injectable({ providedIn: 'root' })
export class VisualGuideManagerService implements OnDestroy {
  readonly possibleGuide$ = new Subject<void>();
  readonly checkForGuide$ = new Subject<GuideRegistration>();
  /** Prepares manager-initiated starts; runtime-only subguide discovery is separate. */
  readonly guideStarting$ = new Subject<GuideRegistration>();
  readonly incomingGuide$ = new Subject<VisualGuideSteps>();
  readonly forcedByEditor$ = new Subject<void>();
  readonly guidesOnPage$ = new BehaviorSubject<string[]>([]);
  private readonly labels$ = new BehaviorSubject<GuideLabel[]>([]);
  readonly currentGuides$ = combineLatest([
    this.labels$,
    this.guidesOnPage$,
  ]).pipe(
    map(([labels, containers]) =>
      labels.filter((label) => containers.includes(label.container)),
    ),
  );
  private readonly destroyed$ = new Subject<void>();
  private readonly requests$ = new Subject<void>();
  private readonly nodesInPlay = new Map<HTMLElement, string>();
  private readonly manualNodes = new Set<HTMLElement>();
  private readonly pendingNodes = new Map<HTMLElement, PendingGuide>();
  private guides: Record<string, VisualGuideSteps> = {};
  private catalogReady = false;

  constructor(
    private readonly guideService: VisualGuideService,
    getAllAvailableVisualGuidesGQL: GetAllAvailableVisualGuidesGQL,
    getMineVisualGuidesState: GetMineVisualGuidesStateGQL,
  ) {
    merge(this.requests$, guideService.stateChanges$)
      .pipe(
        debounceTime(50),
        filter(
          () =>
            this.catalogReady && this.canStart() && this.pendingNodes.size > 0,
        ),
        concatMap(() =>
          getMineVisualGuidesState.watch({}).valueChanges.pipe(
            throwOnQueryError,
            filter((result) => !result.loading && !!result.data),
            filter(hasCompleteQueryData),
            first(),
            map(
              (result) =>
                completeQueryData(result).getAllMineVisualGuideStates || [],
            ),
            tap((states) => {
              // A dialog or another guide can change while the query is in flight.
              if (!this.canStart()) return;
              const candidates: ReadyGuide[] = [];
              for (const [node, pending] of this.pendingNodes) {
                const { container } = pending;
                if (
                  !node.isConnected ||
                  this.manualNodes.has(node) ||
                  this.nodesInPlay.get(node) !== container
                ) {
                  this.pendingNodes.delete(node);
                  continue;
                }
                const guide = this.guides[container];
                if (!guide?.length) {
                  this.pendingNodes.delete(node);
                  continue;
                }
                const alreadyStarted = states.some(
                  (state) =>
                    state?.visualGuideId === guide[0].visualGuideId &&
                    state.started,
                );
                candidates.push({
                  ...pending,
                  node,
                  steps: guide,
                  alreadyStarted,
                });
              }
              const selected = this.selectReadyGuides(candidates);
              const steps = selected.flatMap((candidate) => {
                this.pendingNodes.delete(candidate.node);
                this.guideStarting$.next({
                  visualGuideId: candidate.container,
                  node: candidate.node,
                });
                return candidate.steps.map((step) => ({
                  ...step,
                  node: candidate.node,
                }));
              });
              if (steps.length) {
                this.incomingGuide$.next(steps);
                this.guideService.start(steps);
              }
            }),
            catchError((error) => {
              console.error('Cannot load visual guide progress.', error);
              return EMPTY;
            }),
          ),
        ),
        takeUntil(this.destroyed$),
      )
      .subscribe();

    merge(
      this.forcedByEditor$,
      fromEvent<KeyboardEvent>(document, 'keydown').pipe(
        filter((event) => event.key === 'm' && event.ctrlKey),
      ),
    )
      .pipe(
        tap(() => this.enqueueCurrentNodes()),
        takeUntil(this.destroyed$),
      )
      .subscribe();

    getAllAvailableVisualGuidesGQL
      .watch({})
      .valueChanges.pipe(
        throwOnQueryError,
        filter((result) => !result.loading && !!result.data),
        tap((result) =>
          this.setGuides(
            completeQueryData(result).getAllAvailableVisualGuides || [],
            true,
          ),
        ),
        catchError((error) => {
          console.error('Cannot load visual guide definitions.', error);
          return EMPTY;
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe();
  }

  getNodesInPlay(): ReadonlyMap<HTMLElement, string> {
    return new Map(this.nodesInPlay);
  }

  checkForGuide(container: string, node: HTMLElement, autoStart = true): void {
    if (!container) return;
    const previous = this.nodesInPlay.get(node);
    if (previous && previous !== container) this.removeGuide(previous, node);
    this.nodesInPlay.set(node, container);
    if (autoStart) {
      this.manualNodes.delete(node);
      this.pendingNodes.set(node, {
        container,
        registeredDuringGuide: this.guideService.guideInProgress,
      });
    } else {
      this.manualNodes.add(node);
      this.pendingNodes.delete(node);
    }
    this.publishContainers();
    this.possibleGuide$.next(undefined);
    this.checkForGuide$.next({ visualGuideId: container, node });
    this.requests$.next(undefined);
  }

  removeGuide(container: string, node?: HTMLElement): void {
    const visualGuideId = this.guides[container]?.[0]?.visualGuideId;
    for (const [registeredNode, registeredContainer] of this.nodesInPlay) {
      if (
        registeredContainer !== container ||
        (node && registeredNode !== node)
      )
        continue;
      this.nodesInPlay.delete(registeredNode);
      this.manualNodes.delete(registeredNode);
      this.pendingNodes.delete(registeredNode);
      if (visualGuideId)
        this.guideService.removeSubGuide(visualGuideId, registeredNode);
    }
    this.publishContainers();
  }

  startGuide(container: string): void {
    const guide = this.guides[container];
    const node = [...this.nodesInPlay].find(
      ([element, id]) => id === container && element.isConnected,
    )?.[0];
    if (!guide?.length || !node) return;
    this.pendingNodes.delete(node);
    this.guideStarting$.next({ visualGuideId: container, node });
    this.guideService.start(
      guide.map((step) => ({ ...step, node })),
      true,
    );
  }

  watchForPossibleGuided() {
    return this.possibleGuide$.asObservable();
  }

  setGuides(
    newGuides: ReadonlyArray<GuideDefinition | null>,
    recheckCurrentNodes = false,
  ): void {
    const guides: Record<string, VisualGuideSteps> = {};
    const labels: GuideLabel[] = [];
    for (const guide of newGuides) {
      if (
        !guide?.container ||
        !guide.visualGuideId ||
        guide.deleted ||
        !guide.steps?.length
      )
        continue;
      const steps: VisualGuideSteps = guide.steps
        .filter((step) => !!step)
        .map((step) => ({
          ...step,
          visualGuideId: guide.visualGuideId,
          actions: (step.actions ?? [step.action]).filter(isAction),
          action: isAction(step.action) ? step.action : undefined,
          pauseOnActions: (step.pauseOnActions || []).filter(isAction),
          resumeOnActions: (step.resumeOnActions || []).filter(isAction),
        }));
      if (!steps.length) continue;
      guides[guide.container] = steps;
      labels.push({
        container: guide.container,
        name: guide.name || guide.container,
      });
    }
    this.guides = guides;
    this.labels$.next(labels);
    this.guideService.setAllGuides(guides);
    this.catalogReady = true;
    if (recheckCurrentNodes) this.enqueueCurrentNodes();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(undefined);
    this.destroyed$.complete();
    this.nodesInPlay.clear();
    this.manualNodes.clear();
    this.pendingNodes.clear();
    this.publishContainers();
  }

  private selectReadyGuides(candidates: ReadyGuide[]): ReadyGuide[] {
    if (!candidates.length) return [];
    if (!this.guideService.guideInProgress) {
      // Start a single root. Descendants must enter at that root's wait step,
      // and independent roots remain queued until the active tour finishes.
      let remaining = [...candidates];
      while (remaining.length) {
        const roots = remaining.filter(
          (candidate) =>
            !remaining.some(
              (other) =>
                other.node !== candidate.node &&
                other.node.contains(candidate.node),
            ),
        );
        const descendants = (candidate: ReadyGuide) =>
          remaining.filter(
            (other) =>
              other.node !== candidate.node &&
              candidate.node.contains(other.node),
          ).length;
        const root = roots.reduce((selected, candidate) =>
          descendants(candidate) > descendants(selected) ? candidate : selected,
        );
        if (!root.alreadyStarted) return [root];
        this.pendingNodes.delete(root.node);
        remaining = remaining.filter((candidate) => candidate !== root);
      }
      return [];
    }
    const step = this.guideService.currentStepDetails;
    const anchor =
      step?.node ||
      [...this.nodesInPlay].find(
        ([, container]) =>
          !!step?.visualGuideId &&
          this.guides[container]?.[0]?.visualGuideId === step.visualGuideId,
      )?.[0];
    const registeredNodes = [...this.nodesInPlay]
      .filter(([, container]) => !!this.guides[container]?.length)
      .map(([node]) => node);
    return candidates.filter((candidate) => {
      if (candidate.node === anchor) {
        this.pendingNodes.delete(candidate.node);
        return false;
      }
      if (this.guideService.canAcceptSubGuide(candidate.node, registeredNodes))
        return true;
      // A newly opened dialog can live outside its parent's DOM subtree.
      return (
        candidate.registeredDuringGuide && !this.parentGuideNode(candidate.node)
      );
    });
  }

  private parentGuideNode(node: HTMLElement): HTMLElement | undefined {
    let nearest: HTMLElement | undefined;
    for (const [candidate, container] of this.nodesInPlay) {
      if (
        candidate === node ||
        !candidate.isConnected ||
        !this.guides[container]?.length ||
        !candidate.contains(node)
      )
        continue;
      if (!nearest || nearest.contains(candidate)) nearest = candidate;
    }
    return nearest;
  }

  private canStart(): boolean {
    return (
      !this.guideService.isSavingState &&
      (!this.guideService.guideInProgress ||
        this.guideService.allowInterception)
    );
  }
  private publishContainers(): void {
    this.guideService.setGuideNodes(
      new Map(this.nodesInPlay),
      new Set(this.manualNodes),
    );
    this.guidesOnPage$.next([...new Set(this.nodesInPlay.values())]);
  }
  private enqueueCurrentNodes(): void {
    for (const [node, container] of this.nodesInPlay) {
      if (
        node.isConnected &&
        !this.manualNodes.has(node) &&
        !this.pendingNodes.has(node)
      ) {
        this.pendingNodes.set(node, {
          container,
          registeredDuringGuide: false,
        });
      }
    }
    this.requests$.next(undefined);
  }
}
